import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	GetMatchSearch,
	GetMatchSearchParams,
} from "../../domain/usecases/getMatchSearch";
import { DeleteLike } from "../../domain/usecases/deleteLike";
import { RejectMatch } from "../../domain/usecases/rejectMatch";
import { GetLikes, GetLikesParams } from "../../domain/usecases/getLikes";
import { GetReportSubject } from "../../domain/usecases/getReportSubject";
import { ConnectSocketMatch } from "../../domain/usecases/connectSocket";
import { EmitLike, EmitLikeParams } from "../../domain/usecases/emitLike";
import { OnNotificationLike } from "../../domain/usecases/notificationLike";
import { DisconnectSocketMatch } from "../../domain/usecases/disconnectSocket";
import { LikeParams } from "../../domain/entities/emitLikeParams";
import { addNewMatch, notificationReceived } from "./MatchSlice";
import {
	GetMyMatches,
	GetMyMatchesParams,
} from "../../domain/usecases/getMyMatches";
import { GetMyLikes, GetMyLikesParams } from "../../domain/usecases/getMyLikes";
import { GetMyNewMatches } from "../../domain/usecases/getMyNewMatches";
import { CreateOrderPaypal, CreateOrderPaypalParams } from "../../domain/usecases/createOrderPaypal";
import { SendPaidMessage, SendPaidMessageParams } from "../../domain/usecases/sendPaidMessage";
import { GetUserInfoById, GetUserInfoByIdParams } from "../../domain/usecases/getUserInfoById";
import { DeleteTrashUser } from "../../domain/usecases/deleteTrashUser";
import { DeleteBlockMatch } from "../../domain/usecases/deleteBlockMatch";
import { DeleteBlockMatchParams } from "../../domain/entities/deleteBlockMatchParams";
import { filterUnseenDiscoveryProfiles } from "../utilities/discoverySession";

const MAX_FARTHER_RADIUS = 2000;
const FARTHER_RADIUS_STEP = 400;

const nextFartherRadius = (currentRadius: number): number => {
	const safeRadius = Math.min(Math.max(currentRadius || FARTHER_RADIUS_STEP, 1), MAX_FARTHER_RADIUS);
	if (safeRadius >= MAX_FARTHER_RADIUS) return MAX_FARTHER_RADIUS;
	return Math.min(
		(Math.floor(safeRadius / FARTHER_RADIUS_STEP) + 1) * FARTHER_RADIUS_STEP,
		MAX_FARTHER_RADIUS,
	);
};

export const onGetMatchSearch = createAsyncThunk(
	"match/search",
	async (params: GetMatchSearchParams, { extra }) => {
		const { getMatchSearch } = extra as {
			getMatchSearch: GetMatchSearch;
		};
		if (params.clientMaximumMatches) {
			const maximumMatches = params.clientMaximumMatches;
			const pageLimit = Number(params.params.limit) || 20;
			const collected = [] as Awaited<ReturnType<GetMatchSearch["execute"]>>["matches"];
			let resolvedRadio = Number(params.params.radio) || FARTHER_RADIUS_STEP;
			let page = Number(params.params.page) || 1;
			let lastResult: Awaited<ReturnType<GetMatchSearch["execute"]>> | undefined;
			let hasMoreEligibleWithinRadius = false;

			while (true) {
				for (let scannedPages = 0; scannedPages < 100; scannedPages += 1) {
					lastResult = await getMatchSearch.execute({
						...params,
						params: {
							...params.params,
							page: page.toString(),
							radio: resolvedRadio.toString(),
						},
					});
					const eligible = filterUnseenDiscoveryProfiles(
						lastResult.matches,
						params.clientExcludedUserIds,
						params.clientMinimumDistanceExclusive,
						params.clientMaximumDistanceInclusive,
					);
					for (const profile of eligible) {
						if (collected.some((existing) => existing.id === profile.id)) continue;
						if (collected.length >= maximumMatches) {
							hasMoreEligibleWithinRadius = true;
							break;
						}
						collected.push(profile);
					}
					if (hasMoreEligibleWithinRadius || lastResult.matches.length < pageLimit) break;
					page += 1;
				}
				if (
					collected.length > 0 ||
					!params.clientExpandFartherIfEmpty ||
					resolvedRadio >= MAX_FARTHER_RADIUS
				) {
					break;
				}
				resolvedRadio = nextFartherRadius(resolvedRadio);
				page = 1;
				lastResult = undefined;
			}

			return {
				...(lastResult || { matches: [], page }),
				matches: collected,
				sourceMatchesCount: 0,
				clientHasMoreEligibleWithinRadius: hasMoreEligibleWithinRadius,
				clientResolvedRadio: resolvedRadio,
			};
		}
		const result = await getMatchSearch.execute(params);
		const sourceMatchesCount = result.matches.length;
		return {
			...result,
			matches: filterUnseenDiscoveryProfiles(
				result.matches,
				params.clientExcludedUserIds,
				params.clientMinimumDistanceExclusive,
				params.clientMaximumDistanceInclusive,
			),
			sourceMatchesCount,
		};
	}
);

export const onGetLikes = createAsyncThunk(
	"match/likes",
	async (params: GetLikesParams, { extra }) => {
		const { getLikes } = extra as {
			getLikes: GetLikes;
		};
		const result = await getLikes.execute(params);
		return result;
	}
);

export const onGetMyLikes = createAsyncThunk(
	"match/my-likes",
	async (params: GetMyLikesParams, { extra }) => {
		const { getMyLikes } = extra as {
			getMyLikes: GetMyLikes;
		};
		const result = await getMyLikes.execute(params);
		return result;
	}
);

export const onGetMyMatches = createAsyncThunk(
	"match/my-matches",
	async (params: GetMyMatchesParams, { extra }) => {
		const { getMyMatches } = extra as {
			getMyMatches: GetMyMatches;
		};
		const result = await getMyMatches.execute(params);
		return result;
	}
);

export const onGetMatchesPartner = createAsyncThunk(
	"match/my-matches-partner",
	async (params: GetMyMatchesParams, { extra }) => {
		const { getMyMatches } = extra as {
			getMyMatches: GetMyMatches;
		};
		const result = await getMyMatches.execute(params);
		return result;
	}
);

export const onGetMyNewMatches = createAsyncThunk(
	"match/my-new-matches",
	async (params: GetMyMatchesParams, { extra }) => {
		const { getMyNewMatches } = extra as {
			getMyNewMatches: GetMyNewMatches;
		};
		const result = await getMyNewMatches.execute(params);
		return result;
	}
);

export const onDeleteLike = createAsyncThunk(
	"match/delete-like",
	async (match_id: string, { extra }) => {
		const { deleteLike } = extra as { deleteLike: DeleteLike };
		await deleteLike.execute(match_id);
		return;
	}
);

export const onRejectMatch = createAsyncThunk(
	"match/reject-match",
	async (match_id: string, { extra }) => {
		const { rejectMatch } = extra as { rejectMatch: RejectMatch };
		await rejectMatch.execute(match_id);
		return;
	}
);

export const onGetReportSubject = createAsyncThunk(
	"match/report-subject",
	async (_, { extra }) => {
		const { getReportSubject } = extra as {
			getReportSubject: GetReportSubject;
		};
		const result = await getReportSubject.execute();
		return result;
	}
);

export const onConnectSocket = createAsyncThunk(
	"match/connect-socket",
	async (_, { extra }) => {
		const { connectSocket } = extra as {
			connectSocket: ConnectSocketMatch;
		};
		await connectSocket.execute();
		return;
	}
);

export const onEmitLike = createAsyncThunk(
	"match/emit-like",
	async (params: EmitLikeParams, { extra }) => {
		const { emitLike } = extra as { emitLike: EmitLike };
		await emitLike.execute(params);
		return;
	}
);

export const onNotificationLike = createAsyncThunk(
	"match/start-listening-for-likes",
	async (_, { extra, dispatch }) => {
		const { onNotificationLike } = extra as {
			onNotificationLike: OnNotificationLike;
		};
		onNotificationLike.execute((data: LikeParams) => {
			const isMatch = data.status_user_id1 === "matched" && data.status_user_id2 === "matched";
			dispatch(notificationReceived({
				...data,
				type: isMatch ? "match" : "like",
			}));
			if (isMatch) dispatch(addNewMatch({...data}));
		});
	}
);

export const onDisconnectSocketMatch = createAsyncThunk(
	"match/disconnect-socket",
	async (_, { extra }) => {
		const { disconnectSocketMatch } = extra as {
			disconnectSocketMatch: DisconnectSocketMatch;
		};
		await disconnectSocketMatch.execute();
		return;
	}
);

export const onCreateOrderPaypal = createAsyncThunk(
	"match/create-order-paypal",
	async (params: CreateOrderPaypalParams, { extra }) => {
		const { createOrderPaypal } = extra as { createOrderPaypal: CreateOrderPaypal };
		const result = await createOrderPaypal.execute(params);
		return result;
	}
);

export const onSendPaidMessage = createAsyncThunk(
	"match/paid-message",
	async (params: SendPaidMessageParams, { extra }) => {
		const { sendPaidMessage } = extra as { sendPaidMessage: SendPaidMessage };
		await sendPaidMessage.execute(params);
		return;
	}
);

export const onGetUserInfoById = createAsyncThunk(
	"match/user-info-by-id",
	async (params: GetUserInfoByIdParams, { extra }) => {
		const { getUserInfoById } = extra as {
			getUserInfoById: GetUserInfoById;
		};
		const result = await getUserInfoById.execute(params);
		return result;
	}
);

export const onDeleteTrashUser = createAsyncThunk(
	"match/delete-trash-user",
	async (user_id: string, { extra }) => {
		const { deleteTrashUser } = extra as { deleteTrashUser: DeleteTrashUser };
		await deleteTrashUser.execute(user_id);
		return;
	}
);

export const onDeleteBlockMatch = createAsyncThunk(
	"match/block-match",
	async (params: DeleteBlockMatchParams, { extra }) => {
		const { deleteBlockMatch } = extra as { deleteBlockMatch: DeleteBlockMatch };
		await deleteBlockMatch.execute(params);
		return;
	}
);
